import { Component, computed, inject, Input, OnInit, signal } from "@angular/core";
import { Location } from "@angular/common";
import { SideNavStatusEnum } from "../../interfaces/side-nav-status.enum";
import { SideNavService } from "../../services/side-nav.service";
import { NavigationCancel, NavigationEnd, Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { SidenavModel } from "../../models/sidenav.model";
import { DropdownInputComponent } from "@kernel/form/modules/inputs/simple-inputs/components/dropdown-input/dropdown-input.component";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { environment } from "@environments/environment";
import { ResourcePipe } from "@app/kernel/tools/download-files/downloaded-files.config";
import { ActiveDirectoryAbstraction } from "@app/shared-core/abstractions/active-directory.abstraction";
import { IBaseDirectory } from "@shared-core/interfaces/base-directory.interface";
import { filter } from "rxjs";
import { untilDestroyed } from "@shared-core/helpers/until-destroy.helper";
import { IconsBarComponent } from "../icons-bar/icons-bar.component";
import { LinksNavComponent } from "../links-nav/links-nav.component";
import { DocumentHelpersService } from "@app/shared-core/general/document/document.service";

/**
 * Renders and controls the application side navigation UI.
 *
 * It coordinates route state, side-nav expansion state, and active directory
 * selection while delegating item state mutations to {@link SideNavService}.
 */
@Component({
  selector: "sxw-side-nav",
  templateUrl: "./side-nav.component.html",
  styleUrls: ["./side-nav.component.scss"],
  imports: [IconsBarComponent, TranslatePipe, SvgIconComponent, DropdownInputComponent, ResourcePipe, LinksNavComponent],
})
export class SideNavComponent implements OnInit {
  /**
   * Side navigation state manager.
   */
  readonly sideNavService = inject(SideNavService);
  /**
   * Angular location service used to read the current browser path.
   */
  private location = inject(Location);
  /**
   * Abstraction responsible for listing and switching active directories.
   */
  private activeDirectoryAbstraction = inject(ActiveDirectoryAbstraction);
  /**
   * Document helper service used for viewport-aware side-nav behavior.
   */
  private documentService = inject(DocumentHelpersService);

  /**
   * Router instance used to observe navigation events.
   */
  router = inject(Router);

  /**
   * Fixed side-nav width.
   */
  @Input() width = signal("303px");
  /**
   * Primary brand name displayed in the side-nav header.
   */
  @Input() agencyName = signal("Storex");
  /**
   * Secondary brand name displayed in the side-nav header.
   */
  @Input() agencyExtraName = signal("Web");
  /**
   * CSS class used for the primary brand name.
   */
  @Input() agencyNameStyle = signal("agency-name");
  /**
   * CSS class used for the secondary brand name.
   */
  @Input() agencyExtraNameStyle = signal("agency-extra-name");

  /**
   * Enum reference used in template bindings.
   */
  sideNavStatusEnum = SideNavStatusEnum;
  /**
   * Current side-nav display mode.
   */
  sideNavStatus = signal<SideNavStatusEnum | null>(SideNavStatusEnum.collapse);
  /**
   * Initial URL used to sync active menu state on startup.
   */
  currentUrl = this.location.path() || "/";
  /**
   * Distinguishes navigation triggered by a side-nav click from browser
   * history navigation.
   */
  clickMode = false;

  /**
   * Translation key used for the directory selector placeholder.
   */
  sideNavDropDownPlaceholder = "SIDE_NAV.OTHERS.CHOOSE_ACTIVE_DIRECTORY";

  /**
   * Currently selected active directory.
   */
  activeDirectory = computed(() => this.activeDirectoryAbstraction.activeDirectory());
  /**
   * Directories available for selection.
   */
  availableDirectories = computed(() => this.activeDirectoryAbstraction.availableDirectories());

  /**
   * Indicates whether authentication is bypassed by environment configuration.
   */
  noUser = signal(environment.skip_auth);

  /**
   * Teardown helper for subscriptions created in this component.
   */
  takeUntilDestroy = untilDestroyed();

  /**
   * Initializes menu state and sets up subscriptions that keep side-nav state
   * synchronized with route changes and authorization updates.
   */
  ngOnInit() {
    this.sideNavService.searchMenuActivatedRoute(this.currentUrl);
    // this.updateSideNavStatus();

    this.observeRouteNavigationEnd();
    this.sideNavService
      .observeUserAuthorization()
      .pipe(this.takeUntilDestroy())
      .subscribe(() => this.updateSideNavStatus());
  }

  /**
   * Subscribes to router events and refreshes link state when navigation is
   * not initiated by a local side-nav click.
   */
  private observeRouteNavigationEnd() {
    this.router.events
      .pipe(
        this.takeUntilDestroy(),
        filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel)
      )
      .subscribe(() => {
        if (this.clickMode) {
          this.clickMode = false;
          return;
        }
        this.sideNavService.updateLinksState();
        // this.updateSideNavStatus();
      });
  }

  /**
   * Handles user interaction with a side-nav item by recalculating panel status
   * and applying the corresponding item action.
   *
   * @param item Clicked side-nav item.
   */
  clickItem(item: SidenavModel) {
    this.calculateSideNavStatus(item);
    this.updateSidenavItem(item);
  }

  /**
   * Executes item-specific behavior (custom action, selection, or expansion).
   *
   * @param item Clicked side-nav item.
   */
  private updateSidenavItem(item: SidenavModel) {
    if (item.perform) {
      return item.perform(item);
    }
    this.clickMode = true;
    if (item.parent?.id === "root") {
      this.sideNavService.setSelectedMainLink(item.id);
    } else {
      this.sideNavService.toggleExpandItem(item);
    }
    if (!item.routerLink) this.clickMode = false;
  }

  /**
   * Updates the side-nav panel status based on current status and optional
   * clicked item context.
   *
   * @param item Clicked side-nav item. When omitted, toggles current status.
   */
  calculateSideNavStatus(item?: SidenavModel) {
    this.sideNavStatus.update(status => {
      if (!item) {
        return status === SideNavStatusEnum.collapse ? SideNavStatusEnum.expanded : SideNavStatusEnum.collapse;
      } else if (item?.items.length) {
        if (this.sideNavService.selectedMainLink()?.id === item.id && status === SideNavStatusEnum.expanded) return SideNavStatusEnum.collapse;
        else if (this.sideNavService.selectedMainLink()?.id === item.id && status === SideNavStatusEnum.collapse) return SideNavStatusEnum.expanded;
        else if (this.sideNavService.selectedMainLink()?.id !== item.id) return SideNavStatusEnum.expanded;
        else return SideNavStatusEnum.collapse;
      } else return item.parent?.id === "root" ? SideNavStatusEnum.collapse : status;
    });
  }

  /**
   * Recomputes default side-nav status from current selection and viewport size.
   */
  private updateSideNavStatus() {
    this.sideNavStatus.update(_ => {
      if (this.sideNavService.selectedMainLink()?.items?.length && !this.documentService.isMobile) {
        return SideNavStatusEnum.expanded;
      }
      return SideNavStatusEnum.collapse;
    });
  }

  /**
   * Changes the currently active directory.
   *
   * @param directory Target directory to activate.
   */
  changeActiveDirectory(directory: IBaseDirectory): void {
    this.activeDirectoryAbstraction.changeActiveDirectory(directory.id).subscribe();
  }
}
